import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; 
import { NavBarLogin, RenunganCard } from "../../../components";
import { db, storage } from "../../../config/firebase";
import {
  ref as dbRef,
  set,
  update,
  remove,
  onValue,
  push,
} from "firebase/database";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import "./updateRenungan.scss";

// 🆕 Daftar Pendeta Default
const DEFAULT_PENDETA_LIST = [
  "Pdt. Ria D. Luntungan, S.Th",
  "Pdt. Natalia C. D. Wokas, S.Teol",
  "Pdt. Jovin Killa, S.Th",
];
const OTHER_OPTION = "other";

// Fungsi utilitas untuk memformat tanggal
const getFormattedDate = (date) => {
  const options = { day: 'numeric', month: 'long', year: 'numeric' };
  return new Date(date).toLocaleDateString('id-ID', options);
};

const UpdateRenungan = () => {
  const navigate = useNavigate(); 

  const [renunganList, setRenunganList] = useState([]);
  const [judul, setJudul] = useState("");
  const [penulis, setPenulis] = useState("");
  const [alkitab, setAlkitab] = useState("");
  const [tanggalTampil, setTanggalTampil] = useState(""); 
  const [konten, setKonten] = useState("");

  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [editId, setEditId] = useState(null);
  
  // 🆕 State untuk mengontrol input penulis manual
  const [isOtherPenulis, setIsOtherPenulis] = useState(false); 

  // 🔄 Ambil semua renungan dari Realtime Database
  useEffect(() => {
    // ... (kode useEffect tetap sama)
    const renunganRef = dbRef(db, "renunganHarian");

    const unsubscribe = onValue(renunganRef, (snapshot) => {
      const data = snapshot.val() || {};
      const list = Object.keys(data).map((key) => ({
        id: key,
        ...data[key],
      }));

      setRenunganList(list.reverse());
    });

    return () => unsubscribe();
  }, []);

  // 🔼 Upload Gambar ke Firebase Storage
  const uploadImage = async (file) => {
    // ... (kode uploadImage tetap sama)
    const imageRef = storageRef(
        storage,
        `renunganHarian/${Date.now()}_${file.name}`
    );
    const uploadTask = uploadBytesResumable(imageRef, file);

    return new Promise((resolve, reject) => {
        uploadTask.on(
            "state_changed",
            (snapshot) => {
                const progress =
                    (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setProgress(progress);
            },
            (error) => {
                console.error("Upload error:", error);
                reject(error);
            },
            async () => {
                const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
                resolve(downloadUrl);
            }
        );
    });
  };

  // 💾 Tambah atau Update renungan
  const handleSave = async (e) => {
    e.preventDefault();
    
    // Validasi tambahan untuk Penulis saat mode "Other"
    if (isOtherPenulis && !penulis) {
        alert("Silakan masukkan nama penulis secara manual!");
        return;
    }

    if (!judul || !penulis || !alkitab || !konten) {
      alert("Semua kolom (Judul, Penulis, Pembacaan, Konten) wajib diisi!");
      return;
    }

    let imageUrl = "";

    if (file) {
      imageUrl = await uploadImage(file);
    } else if (editId) {
      const existingRenungan = renunganList.find((r) => r.id === editId);
      imageUrl = existingRenungan ? existingRenungan.gambarUrl : "";
    }
    
    const finalTanggal = editId && tanggalTampil 
                         ? tanggalTampil 
                         : getFormattedDate(new Date());

    const renunganData = {
      judul,
      penulis,
      alkitab,
      tanggal: finalTanggal, 
      gambarUrl: imageUrl || "",
      konten,
      timestamp: Date.now(),
    };

    try {
      if (editId) {
        const updateRef = dbRef(db, `renunganHarian/${editId}`);
        await update(updateRef, renunganData);
        alert("Renungan berhasil diperbarui!");
      } else {
        const newRef = push(dbRef(db, "renunganHarian"));
        await set(newRef, renunganData);
        alert(`Renungan baru untuk tanggal ${finalTanggal} berhasil ditambahkan!`);
      }
    } catch (error) {
      console.error("Error saving data: ", error);
      alert("Gagal menyimpan renungan. Cek konsol.");
    }

    handleCancelEdit();
  };
  
  // 🆕 Handler perubahan dropdown penulis
  const handleChangePenulis = (e) => {
    const selectedValue = e.target.value;
    
    if (selectedValue === OTHER_OPTION) {
        // Jika memilih "Other", aktifkan input manual, dan kosongkan state penulis
        setIsOtherPenulis(true);
        setPenulis(""); 
    } else {
        // Jika memilih nama pendeta, matikan input manual, dan set state penulis
        setIsOtherPenulis(false);
        setPenulis(selectedValue);
    }
  };

  // ✏️ Edit renungan (mengisi form dengan data yang dipilih)
  const handleEdit = (renungan) => {
    setEditId(renungan.id);
    setJudul(renungan.judul);
    setPenulis(renungan.penulis);
    setAlkitab(renungan.alkitab);
    setTanggalTampil(renungan.tanggal); 
    setKonten(renungan.konten);
    
    // 🆕 Logika penentuan apakah penulis yang diedit adalah "Other"
    if (DEFAULT_PENDETA_LIST.includes(renungan.penulis)) {
        setIsOtherPenulis(false);
    } else {
        // Jika nama penulis tidak ada dalam daftar default, anggap itu adalah "Other"
        setIsOtherPenulis(true);
    }
    
    window.scrollTo(0, 0);
  };

  // ❌ Hapus renungan
  const handleDelete = async (id) => {
    // ... (kode handleDelete tetap sama)
    if (window.confirm("Yakin ingin menghapus renungan ini?")) {
      try {
        const deleteRef = dbRef(db, `renunganHarian/${id}`);
        await remove(deleteRef);
        alert("Renungan berhasil dihapus!");
      } catch (error) {
        console.error("Error deleting data: ", error);
        alert("Gagal menghapus renungan. Cek konsol.");
      }
    }
  };

  // Fungsi Reset Form
  const handleCancelEdit = () => {
    setJudul("");
    setPenulis("");
    setAlkitab("");
    setTanggalTampil(""); 
    setKonten("");
    setFile(null);
    setProgress(0);
    setEditId(null);
    setIsOtherPenulis(false); // 🆕 Reset status input manual
  };

  // Fungsi navigasi ke homepage operator
  const handleBackToOperator = () => { 
    navigate('/operator'); 
  };

  // 🆕 Tentukan nilai dropdown saat mode EDIT
  const dropdownValue = editId 
                        ? (DEFAULT_PENDETA_LIST.includes(penulis) ? penulis : OTHER_OPTION)
                        : (isOtherPenulis ? OTHER_OPTION : penulis);

  return (
    <div className="update-renungan-page">
      <NavBarLogin />
      <div className="update-renungan-container">
        {/* TOMBOL KEMBALI KE HOMEPAGE / OPERATOR */}
        <button 
          onClick={handleBackToOperator} 
          className="back-button"
        >
          &larr; Dashboard Operator
        </button>
        
        <h1>Update Renungan Harian</h1>

        <form onSubmit={handleSave} className="upload-form">
          {/* Input Judul */}
          <input
            type="text"
            placeholder="Judul Renungan"
            value={judul}
            onChange={(e) => setJudul(e.target.value)}
          />

          {/* 🔑 Dropdown Penulis */}
          <select 
            onChange={handleChangePenulis}
            // Tentukan nilai yang dipilih. Saat ADD, kosongkan. Saat EDIT, tentukan apakah nama ada di list atau "other".
            value={dropdownValue || ""} 
          >
            <option value="" disabled>-- Pilih Penulis --</option>
            {DEFAULT_PENDETA_LIST.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
            <option value={OTHER_OPTION}>-- Penulis Lain (Ketik Manual) --</option>
          </select>
          
          {/* 🔑 Input Penulis Manual (Hanya Muncul Jika isOtherPenulis TRUE) */}
          {(isOtherPenulis || (editId && !DEFAULT_PENDETA_LIST.includes(penulis))) && (
            <input
              type="text"
              placeholder="Masukkan Nama Penulis Manual"
              // Gunakan nilai state penulis saat ini
              value={penulis}
              onChange={(e) => setPenulis(e.target.value)}
              required={isOtherPenulis} // Wajib diisi jika mode "Other" aktif
              className="manual-penulis-input"
            />
          )}

          {/* Input Pembacaan Alkitab */}
          <input
            type="text"
            placeholder="Pembacaan Alkitab"
            value={alkitab}
            onChange={(e) => setAlkitab(e.target.value)}
          />
          
          {/* Input Tanggal (Otomatis/Edit) */}
          <div className="tanggal-display">
            <label>Tanggal Renungan:</label>
            {editId ? (
              <input
                type="text"
                placeholder="Tanggal (contoh: 19 Oktober 2025)"
                value={tanggalTampil}
                onChange={(e) => setTanggalTampil(e.target.value)}
              />
            ) : (
              <p className="auto-tanggal">
                **Otomatis:** {getFormattedDate(new Date())} 
              </p>
            )}
          </div>

          {/* Textarea Konten */}
          <textarea
            placeholder="Isi Konten (HTML diperbolehkan)"
            rows={8}
            value={konten}
            onChange={(e) => setKonten(e.target.value)}
          />

          {/* Input File & Progress */}
          <input type="file" onChange={(e) => setFile(e.target.files[0])} />

          {progress > 0 && (
            <div className="progress">Upload {Math.round(progress)}%</div>
          )}

          {/* Tombol Simpan/Perbarui */}
          <button type="submit">
            {editId ? "Perbarui Renungan" : "Simpan Renungan"}
          </button>

          {/* Tombol Batalkan Edit */}
          {editId && (
            <button
              type="button"
              onClick={handleCancelEdit}
              className="cancel-button"
            >
              Batalkan Edit
            </button>
          )}
        </form>

        <hr />
        <h2>Daftar Renungan Tersimpan</h2>

        <div className="renungan-list">
          {renunganList.map((r) => (
            <RenunganCard
              key={r.id}
              title={r.judul}
              penulis={r.penulis}
              content={r.konten}
              image={r.gambarUrl}
              onDelete={() => handleDelete(r.id)}
              onEdit={() => handleEdit(r)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default UpdateRenungan;